import {
  type DSButtonProps,
  BREAKPOINTS,
  DSBadge,
  DSButton,
  DSDivider,
  DSFlex,
  DSIcon,
  DSSpacer,
  DSText as DSTextLegacy,
} from '@zoe/ds-web'
import { DSText } from '@zoe/ds-web/next-gen'
import Image from 'next/image'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { FocusScope } from 'react-aria'
import { type PressEvent, Link as AriaLink, Button } from 'react-aria-components'
import styled from 'styled-components'

import { useLocale } from '@/context/localeContext'

import { DSGrid } from '@/components/ds/Grid/Grid'
import { DSLinkText } from '@/components/ds/LinkText/LinkText'

import { appendLeadId } from '@/lib/attribution/leadId'
import { useLeadId } from '@/lib/attribution/useLeadId'
import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { getPathWithoutAnyLocale } from '@/lib/utils/localisationUtils'

import { BANNER_HEIGHT_DESKTOP, BANNER_HEIGHT_MOBILE } from './Banner'
import { NavigationLoginButton } from './NavigationLoginButton'
import {
  type NavLink,
  ProductsAccordion,
  ProductsDropdown,
  focusVisible,
  reducedMotion,
} from './NavigationProductsMenu'

// The mobile header is fixed below the promo banner (when one is shown), so the
// menu panel subtracts the same offset to fill exactly to the viewport bottom.
// Single source for that offset, shared by the nav's top and the panel height.
const mobileBannerOffsetPx = (withTopBanner?: boolean) => (withTopBanner ? BANNER_HEIGHT_MOBILE : 0)

const StyledDesktopMenu = styled.div`
  padding: var(--grid-16) var(--grid-12);
  background: var(--color-surface-neutral);
  @media screen and (max-width: ${BREAKPOINTS.m}) {
    display: none;
  }
`

// Persistent mobile bar: logo + burger stay in this row; the menu panel is
// anchored to its bottom edge and slides down from underneath it.
const StyledMobileBar = styled.div`
  position: relative;
  background: var(--color-surface-neutral);
  padding: var(--grid-16);
  z-index: 1;
  @media screen and (min-width: ${BREAKPOINTS.m}) {
    display: none;
  }
`

/* Fixed-size window anchored at the header's bottom that fills to the viewport
   bottom, so the panel scrolls and its last item (Login) is reachable.
   Height = visible viewport − everything above the clip (the fixed nav's banner
   offset + the header bar): 100dvh − bannerOffset − 100% (100% = bar height).
   - dvh, not vh: on iOS 100vh is the *large* viewport (address bar hidden), so
     vh makes this taller than the screen and the bottom sits behind the chrome.
   - the banner offset must be subtracted too (mobileBannerOffsetPx, the same
     value the nav uses for its top): the nav is fixed at top:bannerHeight, so
     without it the clip overshoots the viewport bottom by the banner height and
     the last row stays clipped even at max scroll. */
const StyledMobilePanelClip = styled.div<{ $open: boolean; $withTopBanner: boolean }>`
  position: absolute;
  left: 0;
  top: 100%;
  width: 100vw;
  height: calc(100dvh - ${({ $withTopBanner }) => mobileBannerOffsetPx($withTopBanner)}px - 100%);
  overflow: hidden;
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: visibility 0.8s;
  ${reducedMotion}
`

const StyledMobilePanel = styled.div<{ $open: boolean }>`
  height: 100%;
  width: 100%;
  background: var(--color-surface-neutral);
  /* #3 Margin/BetweenDifferent around the mobile nav == 16px == grid-16 (the
     margin token isn't exposed as a CSS var in ds-web yet). */
  padding: var(--grid-16);
  overflow-y: auto;
  /* Keep an overscroll/"force pull" inside the panel instead of chaining it
     through to the page behind (which would rubber-band the page into view). */
  overscroll-behavior: contain;
  /* Slide as a single unit from under the header row. */
  transform: translateY(${({ $open }) => ($open ? '0' : '-100%')});
  transition: transform 0.8s cubic-bezier(0.5, 1, 0, 1);
  ${reducedMotion}

  /* Match the focus-ring treatment of the react-aria controls for the
     DSLinkText anchors (which don't use the shared focusVisible helper). */
  a {
    outline: none;
  }
  a[data-focus-visible] {
    outline: 2px solid var(--color-text-primary);
    outline-offset: 2px;
    border-radius: var(--radius-4);
  }
`

// Full-viewport fill behind the open mobile menu so an overscroll/"force pull"
// (or the brief gap while the panel slides in) shows the menu surface, never the
// page underneath. Sits below the banner + nav (z-index 4) and above the page;
// purely decorative, so it ignores pointer events.
const StyledMobileBackdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 3;
  background: var(--color-surface-neutral);
  pointer-events: none;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s;
  ${reducedMotion}
  @media screen and (min-width: ${BREAKPOINTS.m}) {
    display: none;
  }
`

const StyledNavigation = styled.nav<{ $withTopBanner: boolean }>`
  position: fixed;
  right: 0;
  top: 0;
  width: 100vw;
  max-width: 100%;
  z-index: 4;
  top: ${({ $withTopBanner }) => ($withTopBanner ? `${BANNER_HEIGHT_DESKTOP}px` : '0')};
  @media screen and (max-width: ${BREAKPOINTS.m}) {
    top: ${({ $withTopBanner }) => mobileBannerOffsetPx($withTopBanner)}px;
  }
`

// Styles the desktop flat-link anchors (DSLinkText) — same navigation colour /
// hover / keyboard-focus treatment as the products popover, so all desktop nav
// links match. The ProductsDropdown trigger is a button (not an anchor) and its
// popover is portaled out, so only the flat links are affected.
const StyledDesktopNav = styled.nav`
  a {
    display: flex;
    align-items: center;
    outline: none;
  }
  a span {
    color: var(--color-text-link-navigation-bold);
    transition: color 0.2s ease-in-out;
  }
  a[data-hovered] span {
    color: var(--color-text-link-navigation-bold-hover);
  }
  a[data-focus-visible] {
    outline: 2px solid var(--color-text-primary);
    outline-offset: 2px;
    border-radius: var(--radius-4);
  }
`

const StyledLogo = styled(Image)`
  display: block;
  z-index: 2;
`

const StyledLogoLink = styled(AriaLink)`
  z-index: 2;
  ${focusVisible}
`

const StyledMenuIconButton = styled(Button)`
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--grid-4);
  color: var(--color-text-primary);
  ${focusVisible}
`

// Designer note: 'Menu' CTA replaced with a burger that morphs into an X in
// place (it stays in the header row alongside the logo).
const StyledMenuToggleIcon = styled.span<{ $open: boolean }>`
  position: relative;
  display: block;
  width: 24px;
  height: 16px;

  span {
    position: absolute;
    left: 0;
    height: 2px;
    width: 100%;
    background: currentColor;
    border-radius: 2px;
    transition:
      transform 0.25s ease,
      opacity 0.2s ease,
      top 0.25s ease;
    ${reducedMotion}
  }
  span:nth-child(1) {
    top: ${({ $open }) => ($open ? '7px' : '0')};
    transform: ${({ $open }) => ($open ? 'rotate(45deg)' : 'none')};
  }
  span:nth-child(2) {
    top: 7px;
    opacity: ${({ $open }) => ($open ? 0 : 1)};
  }
  span:nth-child(3) {
    top: ${({ $open }) => ($open ? '7px' : '14px')};
    transform: ${({ $open }) => ($open ? 'rotate(-45deg)' : 'none')};
  }
`

const StyledButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: auto;
`
const StyledOverlayAnchor = styled.a`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  ${focusVisible}
`

interface CustomNavigationProps {
  links: NavLink[]
  productItems: NavLink[]
  productsLabel?: string
  onProductsToggle?: () => void
  logoVariant: 'brand' | 'primary'
  secondaryCtaLabel?: string
  secondaryCtaHref?: string
  ctaLabel?: string
  ctaHref?: string
  onCtaPress?: (event: PressEvent) => void
  onMenuPress?: () => void
  onLogoPress?: (event: PressEvent) => void
  onSecondaryCtaPress?: (event: PressEvent) => void

  withTopBanner?: boolean
}

type NavButtonProps = {
  label: string
  href?: string
  onPress?: (event: PressEvent) => void
  variant: DSButtonProps['variant']
  size?: DSButtonProps['size']
  icon?: React.ComponentProps<typeof DSIcon>['name']
  'data-testid'?: string
}

// Nav button used for the page-level header CTAs (e.g. "Get Daily30",
// "Select plan"). The real, focusable control is the overlaid <a href> (keeps
// link semantics: new-tab, right-click, crawlable); the DSButton is decorative
// and excluded from the tab order + a11y tree so there's a single tab stop.
export const NavButton = ({
  label,
  href,
  onPress,
  variant,
  size = 'small',
  icon,
  'data-testid': dataTestId,
}: NavButtonProps) => {
  const ctaOverlayRef = useRef<HTMLAnchorElement>(null)
  const router = useTrackedRouter()
  const leadId = useLeadId()

  const handleCtaOnPress = (event: React.MouseEvent) => {
    event.preventDefault()

    const currentHref = ctaOverlayRef.current?.getAttribute('href') || href || ''
    if (onPress) {
      onPress(event as unknown as PressEvent)
    }

    if (currentHref?.includes('#')) {
      const anchor = currentHref.split('#')[1]
      const element = document.getElementById(anchor)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
        return
      }
    }

    const isExternalUrl = currentHref?.startsWith('http') || currentHref?.startsWith('//')
    if (isExternalUrl) {
      window.location.assign(currentHref)
    } else if (currentHref) {
      void router.push(currentHref)
    }
  }

  return (
    <StyledButtonWrapper>
      <DSButton
        variant={variant}
        colorScheme="default"
        size={size}
        width="auto"
        style={{ textWrap: 'nowrap' }}
        testId={dataTestId ?? `navigation-cta-${label?.toLowerCase()?.replace(/ /g, '-')}`}
        excludeFromTabOrder
        aria-hidden="true"
      >
        <DSFlex direction="row" alignItems="center" gap={8}>
          {icon && <DSIcon name={icon} size={16} aria-hidden="true" />}
          <DSTextLegacy
            as="span"
            variant="fluid-paragraph-200"
            weight="semiBold"
            color="primary"
            breakAt={{ breakPoint: 'm', variant: 'fluid-label-100' }}
          >
            {label}
          </DSTextLegacy>
        </DSFlex>
      </DSButton>
      <StyledOverlayAnchor
        ref={ctaOverlayRef}
        href={href && appendLeadId(href, leadId)}
        aria-label={label}
        onClick={handleCtaOnPress}
      />
    </StyledButtonWrapper>
  )
}

const HeaderActions = ({
  ctaLabel,
  ctaHref,
  onCtaPress,
  secondaryCtaLabel,
  secondaryCtaHref,
  onSecondaryCtaPress,
}: Pick<
  CustomNavigationProps,
  'ctaLabel' | 'ctaHref' | 'onCtaPress' | 'secondaryCtaLabel' | 'secondaryCtaHref' | 'onSecondaryCtaPress'
>) => (
  <DSFlex direction="row">
    <NavigationLoginButton />

    {secondaryCtaLabel && secondaryCtaHref && (
      <>
        <DSSpacer direction="horizontal" size={8} />
        <NavButton
          href={secondaryCtaHref}
          label={secondaryCtaLabel}
          onPress={onSecondaryCtaPress}
          variant="secondary"
        />
      </>
    )}

    {ctaLabel && ctaHref && (
      <>
        <DSSpacer direction="horizontal" size={8} />
        <NavButton href={ctaHref} label={ctaLabel} onPress={onCtaPress} variant="primary" />
      </>
    )}
  </DSFlex>
)

export const Navigation = ({
  links,
  productItems,
  productsLabel = 'Products',
  onProductsToggle,
  logoVariant,
  ctaLabel,
  ctaHref,
  onCtaPress,
  onMenuPress,
  onLogoPress,
  secondaryCtaLabel,
  secondaryCtaHref,
  onSecondaryCtaPress,
  withTopBanner,
}: CustomNavigationProps) => {
  const [isSMenuOpen, setIsSMenuOpen] = useState(false)
  const router = useTrackedRouter()
  const { baseLocale } = useLocale()
  const leadId = useLeadId()
  const homeHref = appendLeadId(`/${baseLocale}`, leadId)

  const burgerRef = useRef<HTMLButtonElement>(null)
  const wasMenuOpen = useRef(false)

  const isActive = useCallback(
    (href: string) => router.pathname.includes(getPathWithoutAnyLocale(href)) || router.pathname === href,
    [router.pathname],
  )

  // Close the mobile menu after a client-side navigation.
  useEffect(() => {
    setIsSMenuOpen(false)
  }, [router.asPath])

  // Lock body scroll and close on Escape while the menu is open. Focus
  // containment is handled by FocusScope (kept mounted with contain toggled so
  // the open/close transitions aren't interrupted by a remount).
  useEffect(() => {
    if (!isSMenuOpen) {
      return
    }
    // iOS-proof scroll lock: overflow:hidden on <body> doesn't stop touch
    // scrolling/rubber-band in iOS Safari, so pin the body with position:fixed at
    // the negative current scroll offset (works in Chrome too), then restore the
    // scroll position when the menu closes.
    const { scrollY } = window
    const { body } = document
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    }
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'
    body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSMenuOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      body.style.position = previous.position
      body.style.top = previous.top
      body.style.width = previous.width
      body.style.overflow = previous.overflow
      window.scrollTo(0, scrollY)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSMenuOpen])

  // Restore focus to the burger when the menu closes (FocusScope's restoreFocus
  // only fires on unmount, and we keep it mounted for the transitions).
  useEffect(() => {
    if (wasMenuOpen.current && !isSMenuOpen) {
      burgerRef.current?.focus()
    }
    wasMenuOpen.current = isSMenuOpen
  }, [isSMenuOpen])

  const handleMenuToggle = useCallback(() => {
    setIsSMenuOpen((open) => {
      if (!open) {
        onMenuPress?.()
      }
      return !open
    })
  }, [onMenuPress])

  const logo = useCallback(
    (width: number, height: number) => (
      <StyledLogo src={`/assets/img/ds/zoe-logo-${logoVariant}.svg`} alt="" width={width} height={height} />
    ),
    [logoVariant],
  )

  const mobileMenu = (
    <>
      <nav aria-label="Mobile navigation">
        {productItems.length > 0 && (
          <ProductsAccordion
            // Remount each time the menu opens so the accordion always
            // starts expanded (collapsed state isn't kept across closes).
            key={isSMenuOpen ? 'menu-open' : 'menu-closed'}
            label={productsLabel}
            items={productItems}
            isActive={isActive}
            onOpenTrack={onProductsToggle}
            defaultOpen
          />
        )}
        {links.map((link, index) => (
          <div key={index} id={`mobile-nav-link-${link.text?.toLowerCase()?.replace(/ /g, '-')}`}>
            <DSLinkText
              href={link.href}
              colourScheme="navigation"
              onPress={link.onPress}
              aria-current={isActive(link.href) ? 'page' : undefined}
            >
              <DSText as="span" variant="paragraphLHero" color="default">
                {link.text}
              </DSText>
              {link.badge && <DSSpacer direction="horizontal" size={12} />}
              {link.badge && (
                <DSBadge surfaceColor="yellow-bold" textColor="primary" size="regular">
                  {link.badge}
                </DSBadge>
              )}
            </DSLinkText>
            <DSDivider spacing={24} />
          </div>
        ))}
      </nav>
      <DSSpacer direction="vertical" size={16} />
      <div>
        <HeaderActions
          ctaLabel={ctaLabel}
          ctaHref={ctaHref}
          onCtaPress={onCtaPress}
          secondaryCtaLabel={secondaryCtaLabel}
          secondaryCtaHref={secondaryCtaHref}
          onSecondaryCtaPress={onSecondaryCtaPress}
        />
      </div>
    </>
  )

  // Header row + panel. While the menu is open this is wrapped in a FocusScope
  // so Tab is trapped across both the burger/X and the menu contents.
  const mobileBar = (
    <>
      <DSFlex direction="row" justifyContent="space-between" alignItems="center">
        <StyledLogoLink href={homeHref} onPress={onLogoPress} aria-label="ZOE Home">
          {logo(70, 24)}
        </StyledLogoLink>
        <DSFlex direction="row" alignItems="center" gap={12}>
          {ctaLabel && ctaHref && (
            <NavButton href={ctaHref} label={ctaLabel} onPress={onCtaPress} variant="primary" size="small" />
          )}
          <StyledMenuIconButton
            ref={burgerRef}
            onPress={handleMenuToggle}
            aria-expanded={isSMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isSMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <StyledMenuToggleIcon $open={isSMenuOpen} aria-hidden="true">
              <span />
              <span />
              <span />
            </StyledMenuToggleIcon>
          </StyledMenuIconButton>
        </DSFlex>
      </DSFlex>

      <StyledMobilePanelClip
        id="mobile-menu"
        $open={isSMenuOpen}
        $withTopBanner={!!withTopBanner}
        aria-hidden={!isSMenuOpen}
      >
        <StyledMobilePanel $open={isSMenuOpen}>{mobileMenu}</StyledMobilePanel>
      </StyledMobilePanelClip>
    </>
  )

  return (
    <>
      <StyledMobileBackdrop $open={isSMenuOpen} aria-hidden="true" />
      <StyledNavigation aria-label="Main navigation" id="main-nav" $withTopBanner={!!withTopBanner}>
        <StyledDesktopMenu>
          <DSGrid>
            <DSFlex direction="row" justifyContent="space-between" alignItems="center" width="100%">
              <DSFlex direction="row" alignItems="center">
                <StyledLogoLink href={homeHref} onPress={onLogoPress} aria-label="ZOE Home">
                  {logo(90, 32)}
                </StyledLogoLink>
                <DSSpacer direction="horizontal" size={32} breakAt={{ l: 12 }} />
                <StyledDesktopNav aria-label="Desktop navigation">
                  <DSFlex alignItems="center" direction="row">
                    {productItems.length > 0 && (
                      <>
                        <ProductsDropdown
                          label={productsLabel}
                          items={productItems}
                          isActive={isActive}
                          onOpenTrack={onProductsToggle}
                        />
                        <DSSpacer direction="horizontal" size={24} breakAt={{ l: 12 }} />
                      </>
                    )}
                    {links.map((link, index) => (
                      <Fragment key={index}>
                        <div id={`nav-link-${link.text?.toLowerCase()?.replace(/ /g, '-')}`}>
                          <DSLinkText
                            href={link.href}
                            colourScheme="navigation"
                            onPress={link.onPress}
                            aria-current={isActive(link.href) ? 'page' : undefined}
                          >
                            <DSTextLegacy
                              as="span"
                              variant="fluid-paragraph-300"
                              weight={link.textWeight || 'light'}
                              color="primary"
                              align="center"
                            >
                              {link.text}
                            </DSTextLegacy>
                            {link.badge && <DSSpacer direction="horizontal" size={12} />}
                            {link.badge && (
                              <DSBadge surfaceColor="yellow-bold" textColor="primary" size="regular">
                                {link.badge}
                              </DSBadge>
                            )}
                          </DSLinkText>
                        </div>
                        <DSSpacer direction="horizontal" size={24} breakAt={{ l: 12 }} />
                      </Fragment>
                    ))}
                  </DSFlex>
                </StyledDesktopNav>
              </DSFlex>
              <div>
                <HeaderActions
                  ctaLabel={ctaLabel}
                  ctaHref={ctaHref}
                  onCtaPress={onCtaPress}
                  secondaryCtaLabel={secondaryCtaLabel}
                  secondaryCtaHref={secondaryCtaHref}
                  onSecondaryCtaPress={onSecondaryCtaPress}
                />
              </div>
            </DSFlex>
          </DSGrid>
        </StyledDesktopMenu>

        <StyledMobileBar>
          {/* Kept mounted (so the open/close transitions aren't interrupted by a
            remount); contain traps Tab across the burger/X and the menu only
            while open. */}
          <FocusScope contain={isSMenuOpen}>{mobileBar}</FocusScope>
        </StyledMobileBar>
      </StyledNavigation>
    </>
  )
}
